import { useEffect, useState } from 'react';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  IconButton,
  MenuItem,
  Modal,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  adminEditCleared,
  adminEditSaved,
  adminSaveReset,
  type ApprovalStatus,
  type PurchaseLink,
  type ReleaseEdit,
} from '../store/slices/admin-slice';
import { selectAdminSaveState } from '../store/selectors/admin-selectors';
import { saveAdminEditThunk } from '../store/thunks/admin-thunks';
import { ReleaseItemPreview } from './ReleaseItemPreview';

interface ReleaseEditDrawerProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly recordId: string | undefined;
  readonly initialValues: ReleaseEdit;
  readonly hasExistingEdit: boolean;
  /**
   * Target record's `date` PK on the Releases table (`YYYY-MM-DD`).
   * Required for the server-side save round-trip
   * (`content-ingestion-pipeline` Sub-R5.1).
   */
  readonly recordDate: string | undefined;
  /** Current `metadata.version` of the record. */
  readonly recordVersion: number | undefined;
}

const STATUS_OPTIONS: ReadonlyArray<{ readonly value: ApprovalStatus; readonly label: string }> = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

/**
 * Known platform identifiers — must match the seven keys of
 * `BrandingTokens.platformIcons` so `<PlatformIcons>` resolves the
 * matching artwork for each one. Order is intentional: the most
 * common console / PC choices first, mobile + the unknown
 * fallback last.
 */
const KNOWN_PLATFORMS: ReadonlyArray<{ readonly id: string; readonly label: string }> = [
  { id: 'pc', label: 'PC' },
  { id: 'playstation', label: 'PlayStation' },
  { id: 'xbox', label: 'Xbox' },
  { id: 'nintendoSwitch', label: 'Nintendo Switch' },
  { id: 'steamDeck', label: 'Steam Deck' },
  { id: 'mobile', label: 'Mobile' },
];

const EMPTY_PURCHASE_LINK: PurchaseLink = { vendor: '', url: '' };

/**
 * `<ReleaseEditDrawer>` — admin-mode-only edit modal for a single
 * release record. Live preview panel renders the `<ReleaseCard>`-
 * shaped tile with the in-progress values; the right panel exposes
 * the full edit form:
 *
 *   - Title + description.
 *   - Approval status select.
 *   - Cover image URL (with thumbnail preview).
 *   - Platform checkboxes (the seven known platforms — PC,
 *     PlayStation, Xbox, Nintendo Switch, Steam Deck, Mobile).
 *   - Gallery: dynamic list of image URLs.
 *   - Videos: dynamic list of video URLs.
 *   - Purchase links: dynamic list of `(vendor, url)` rows. The
 *     first entry drives the release card's Purchase button; the
 *     rest land on the future Game-page detail surface.
 */
export function ReleaseEditDrawer({
  open,
  onClose,
  recordId,
  initialValues,
  hasExistingEdit,
  recordDate,
  recordVersion,
}: ReleaseEditDrawerProps): JSX.Element {
  const dispatch = useAppDispatch();
  const [values, setValues] = useState<ReleaseEdit>(initialValues);
  const saveState = useAppSelector((state) => selectAdminSaveState(state, recordId));
  const isSaving = saveState.status === 'saving';
  const canSave = Boolean(recordId) && Boolean(recordDate) && typeof recordVersion === 'number';
  useEffect(() => {
    if (open) {
      setValues({
        ...initialValues,
        purchaseLinks:
          initialValues.purchaseLinks.length > 0
            ? initialValues.purchaseLinks
            : [EMPTY_PURCHASE_LINK],
      });
      if (recordId) dispatch(adminSaveReset({ id: recordId }));
    }
  }, [open, initialValues, recordId, dispatch]);
  const setField =
    <K extends keyof ReleaseEdit>(key: K) =>
    (value: ReleaseEdit[K]): void =>
      setValues((prev) => ({ ...prev, [key]: value }));
  const togglePlatform = (platformId: string): void =>
    setValues((prev) => {
      const present = prev.platforms.includes(platformId);
      const next = present
        ? prev.platforms.filter((p) => p !== platformId)
        : [...prev.platforms, platformId];
      return { ...prev, platforms: next };
    });
  // Generic helpers for the gallery / videos URL lists — both are
  // shaped as `ReadonlyArray<string>` so the same add / set / remove
  // closures work for either field via the `key` discriminator.
  const setUrlField = (key: 'gallery' | 'videos', index: number) =>
    (event: React.ChangeEvent<HTMLInputElement>): void =>
      setValues((prev) => {
        const next = [...prev[key]];
        next[index] = event.target.value;
        return { ...prev, [key]: next };
      });
  const addUrl = (key: 'gallery' | 'videos'): void =>
    setValues((prev) => ({ ...prev, [key]: [...prev[key], ''] }));
  const removeUrl = (key: 'gallery' | 'videos', index: number): void =>
    setValues((prev) => ({
      ...prev,
      [key]: prev[key].filter((_, i) => i !== index),
    }));
  const setPurchaseLinkField = (index: number, key: keyof PurchaseLink) =>
    (event: React.ChangeEvent<HTMLInputElement>): void =>
      setValues((prev) => {
        const next = [...prev.purchaseLinks];
        next[index] = { ...next[index], [key]: event.target.value };
        return { ...prev, purchaseLinks: next };
      });
  const addPurchaseLink = (): void =>
    setValues((prev) => ({ ...prev, purchaseLinks: [...prev.purchaseLinks, EMPTY_PURCHASE_LINK] }));
  const removePurchaseLink = (index: number): void =>
    setValues((prev) => {
      const next = prev.purchaseLinks.filter((_, i) => i !== index);
      return { ...prev, purchaseLinks: next.length > 0 ? next : [EMPTY_PURCHASE_LINK] };
    });
  const handleSave = (): void => {
    if (!recordId || !canSave || !recordDate || typeof recordVersion !== 'number') return;
    // Strip empty rows from each list before persisting.
    const cleanGallery = values.gallery.filter((url) => url.trim().length > 0);
    const cleanVideos = values.videos.filter((url) => url.trim().length > 0);
    const cleanPurchaseLinks = values.purchaseLinks.filter(
      (l) => l.vendor.trim().length > 0 || l.url.trim().length > 0,
    );
    const cleaned: ReleaseEdit = {
      ...values,
      kind: 'release',
      gallery: cleanGallery,
      videos: cleanVideos,
      purchaseLinks: cleanPurchaseLinks,
    };
    dispatch(adminEditSaved({ id: recordId, edit: cleaned }));
    void dispatch(
      saveAdminEditThunk({
        tableName: 'releases',
        uuid: recordId,
        date: recordDate,
        patch: buildReleasePatch(cleaned),
        expectedVersion: recordVersion,
        refetchDate: recordDate,
      }),
    );
  };
  const handleReset = (): void => {
    if (!recordId) return;
    dispatch(adminEditCleared({ id: recordId }));
    onClose();
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      slots={{ backdrop: Backdrop }}
      slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.85)' } } }}
      aria-labelledby="release-edit-modal-title"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95vw', md: 'min(1080px, 92vw)' },
          maxHeight: '92vh',
          outline: 'none',
        }}
      >
        <Paper
          elevation={24}
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            borderRadius: 2,
            overflow: 'hidden',
            maxHeight: '92vh',
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              p: { xs: 2, md: 3 },
              borderRight: { md: '1px solid' },
              borderBottom: { xs: '1px solid', md: 'none' },
              borderColor: { xs: 'divider', md: 'divider' },
              overflow: 'auto',
              backgroundColor: 'background.paper',
            }}
          >
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Live preview
            </Typography>
            <ReleaseItemPreview values={values} />
          </Box>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              p: { xs: 2, md: 3 },
              overflow: 'auto',
              backgroundColor: 'background.default',
            }}
          >
            <Stack spacing={2}>
              <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography
                  id="release-edit-modal-title"
                  variant="h2"
                  component="h2"
                  sx={{ fontSize: '1.25rem' }}
                >
                  Edit release
                </Typography>
                <IconButton onClick={onClose} aria-label="Close edit dialog" size="small">
                  <CloseIcon />
                </IconButton>
              </Stack>
              {saveState.status === 'saved' && (
                <Alert severity="success">Saved. Refetching content…</Alert>
              )}
              {saveState.status === 'failed' && (
                <Alert severity="error">Save failed: {saveState.error ?? 'unknown error'}</Alert>
              )}
              {!canSave && recordId && (
                <Alert severity="warning">
                  Save unavailable — record is missing a version or date on the wire.
                </Alert>
              )}
              <TextField
                select
                label="Approval status"
                value={values.status}
                onChange={(e) => setField('status')(e.target.value as ApprovalStatus)}
                size="small"
              >
                {STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Title"
                fullWidth
                value={values.title}
                onChange={(e) => setField('title')(e.target.value)}
              />
              <TextField
                label="Description"
                fullWidth
                multiline
                minRows={3}
                maxRows={8}
                value={values.body}
                onChange={(e) => setField('body')(e.target.value)}
              />
              <TextField
                label="Cover image URL"
                fullWidth
                value={values.image}
                onChange={(e) => setField('image')(e.target.value)}
                placeholder="https://"
              />
              {values.image && (
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1,
                    backgroundColor: 'background.paper',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Cover image preview
                  </Typography>
                  <Box
                    component="img"
                    src={values.image}
                    alt="Cover image preview"
                    sx={{
                      width: '100%',
                      maxHeight: 160,
                      objectFit: 'contain',
                      borderRadius: 1,
                    }}
                  />
                </Box>
              )}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Platforms
                </Typography>
                <FormGroup row>
                  {KNOWN_PLATFORMS.map((platform) => (
                    <FormControlLabel
                      key={platform.id}
                      control={
                        <Checkbox
                          size="small"
                          checked={values.platforms.includes(platform.id)}
                          onChange={() => togglePlatform(platform.id)}
                        />
                      }
                      label={<Typography variant="body2">{platform.label}</Typography>}
                      sx={{ mr: 2 }}
                    />
                  ))}
                </FormGroup>
              </Box>
              <Stack spacing={1}>
                <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle2">Gallery images</Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={() => addUrl('gallery')}>
                    Add image
                  </Button>
                </Stack>
                {values.gallery.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    No gallery images yet — add a screenshot URL to include it on the Game-page detail surface.
                  </Typography>
                )}
                {values.gallery.map((url, index) => (
                  <Stack key={index} direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
                    <TextField
                      size="small"
                      label={`Image ${index + 1}`}
                      value={url}
                      onChange={setUrlField('gallery', index)}
                      placeholder="https://"
                      sx={{ flex: 1 }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => removeUrl('gallery', index)}
                      aria-label={`Remove gallery image ${index + 1}`}
                      sx={{ mt: 0.5 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
              <Stack spacing={1}>
                <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle2">Videos</Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={() => addUrl('videos')}>
                    Add video
                  </Button>
                </Stack>
                {values.videos.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    No videos yet — add a YouTube or direct mp4 URL to include it on the Game-page detail surface.
                  </Typography>
                )}
                {values.videos.map((url, index) => (
                  <Stack key={index} direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
                    <TextField
                      size="small"
                      label={`Video ${index + 1}`}
                      value={url}
                      onChange={setUrlField('videos', index)}
                      placeholder="https://"
                      sx={{ flex: 1 }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => removeUrl('videos', index)}
                      aria-label={`Remove video ${index + 1}`}
                      sx={{ mt: 0.5 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
              <Stack spacing={1}>
                <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle2">Purchase links</Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={addPurchaseLink}>
                    Add link
                  </Button>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  The first link drives the release card's Purchase button; additional links land on the Game-page detail surface.
                </Typography>
                {values.purchaseLinks.map((link, index) => (
                  <Stack key={index} direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
                    <TextField
                      label="Vendor"
                      size="small"
                      value={link.vendor}
                      onChange={setPurchaseLinkField(index, 'vendor')}
                      placeholder="Steam, Amazon, …"
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      label="URL"
                      size="small"
                      value={link.url}
                      onChange={setPurchaseLinkField(index, 'url')}
                      placeholder="https://"
                      sx={{ flex: 2 }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => removePurchaseLink(index)}
                      aria-label={`Remove purchase link ${index + 1}`}
                      sx={{ mt: 0.5 }}
                      disabled={values.purchaseLinks.length === 1 && !link.vendor && !link.url}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
              <Box sx={{ flexGrow: 1 }} />
              <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end', pt: 1 }}>
                {hasExistingEdit && (
                  <Button color="inherit" onClick={handleReset} disabled={isSaving}>
                    Reset to original
                  </Button>
                )}
                <Button variant="outlined" onClick={onClose} disabled={isSaving}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={!canSave || isSaving}
                  startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
                >
                  {isSaving ? 'Saving…' : 'Save'}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Paper>
      </Box>
    </Modal>
  );
}

/**
 * Project a `ReleaseEdit` (post-cleaning) onto the JSON envelope
 * patch. `status` transitions are omitted — they belong to
 * ApprovalManager (approve / reject).
 */
function buildReleasePatch(cleaned: ReleaseEdit): Record<string, unknown> {
  const patch: Record<string, unknown> = {
    title: cleaned.title,
    body: cleaned.body,
    image: cleaned.image,
  };
  if (cleaned.platforms.length > 0) patch.platforms = [...cleaned.platforms];
  if (cleaned.gallery.length > 0) patch.gallery = [...cleaned.gallery];
  if (cleaned.videos.length > 0) patch.videos = [...cleaned.videos];
  if (cleaned.purchaseLinks.length > 0) {
    patch.purchaseLinks = cleaned.purchaseLinks.map((l) => ({ vendor: l.vendor, url: l.url }));
  }
  return patch;
}
